import * as React from 'react'
import { ButtonGroup, Menu, MenuItem, Stack } from '@mui/material'
import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faBars,
    faFolder,
    faStar,
    faBellSlash,
    faPenToSquare,
    faFileExport,
    faTrash,
    faObjectSubtract,
    faBell,
} from '@fortawesome/pro-duotone-svg-icons'

import ShowEntries from '@/shared/ui/show-entries/ShowEntries'
import MultiContentDossierDelete from '@/feature/DefDossier/DefDossierToolbar/MultiContentDossierDelete'
import ContentDossierExport from '@/feature/DefDossier/DefDossierToolbar/ContentDossierExport'
import MultiContentDossierArchive from '@/feature/DefDossier/DefDossierToolbar/MultiContentDossierArchive'
import MultiContentDossierMerge from '@/feature/DefDossier/MergeDossier/index'
import MultiContentDossierSubscribe from '@/feature/DefDossier/DefDossierToolbar/MultiContentDossierSubscribe'
import MultiContentDossierFavorite from '@/feature/DefDossier/DefDossierToolbar/MultiContentDossierFavorite'
import MultiContentDossierMassUpdate from '@/feature/DefDossier/DefDossierToolbar/MultiContentDossierMassUpdate'
import { StyledButton } from '@/shared/ui/buttons'

interface ActionButtonsProps {
    selected: any[]
    selectedDossiers: any[]
    isAllowedDd: (permission: string) => boolean
    hasSubscriptions: boolean
    dearchivedDossiers: boolean
    archivedDossiers: boolean
    kedo: any
    props: any
    mode: string
    dossiers: Array<any>
    pager: any
    limit: any
    handleEntriesChange: any
    onDeleteSuccess: any
    defDossierId: number
    getSearchFilter: any
    fetchDossierResults: any
    subscriptions: Array<any>
    favorites: Array<any>
    contents: any
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
    selected,
    selectedDossiers,
    isAllowedDd,
    hasSubscriptions,
    dearchivedDossiers,
    archivedDossiers,
    kedo,
    props,
    mode,
    dossiers,
    pager,
    limit,
    handleEntriesChange,
    onDeleteSuccess,
    defDossierId,
    getSearchFilter,
    fetchDossierResults,
    subscriptions,
    favorites,
    contents,
}) => {
    const [showActionModal, setShowActionModal] = useState('')
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [deleteItemName, setDeleteItemName] = useState<string | undefined>()

    const getDeleteItemName = () => {
        if (selectedDossiers.length <= 0) {
            return undefined
        }

        const firstSelectedDossier = selectedDossiers[0]
        const defDossier =
            props.defDossier || kedo.env().getDefDossierById(defDossierId)
        const defDossierName = defDossier
            ? kedo.env().translateItem(defDossier, 'defdossier')
            : null
        const summary = firstSelectedDossier?.summary

        if (selectedDossiers.length > 1) {
            const label = [
                defDossierName || null,
                summary ? `(${kedo.t('for_example')} ${summary})` : null,
            ]
                .filter(Boolean)
                .join(' ')

            return label || undefined
        }

        const label = [defDossierName, summary].filter(Boolean).join(' / ')

        return label || undefined
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const handleClick = (event: React.MouseEvent, action: string) => {
        event.preventDefault()
        setAnchorEl(null)
        if (selectedDossiers.length <= 0) {
            alert(kedo.t('Select dossiers first'))
            return
        }

        if (action === 'delete') {
            setDeleteItemName(getDeleteItemName())
        }

        setShowActionModal(action)
    }
    const handleMenuSelect = (action: string) => {
        setAnchorEl(null)
        setShowActionModal(action)
    }

    return (
        <>
            {selected.length > 0 && (
                <ButtonGroup size="small" variant="outlined">
                    {isAllowedDd('delete') && (
                        <StyledButton
                            variant={'outlined'}
                            color="secondary"
                            invert
                            onClick={(e) => handleClick(e, 'delete')}
                            sx={{
                                '& svg': { mr: 1 },
                            }}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                            {kedo.t('Delete')}
                        </StyledButton>
                    )}

                    {(isAllowedDd('export') ||
                        isAllowedDd('export_word') ||
                        isAllowedDd('export_csv') ||
                        isAllowedDd('export_pdf') ||
                        isAllowedDd('export_html')) && (
                        <StyledButton
                            variant={'outlined'}
                            color="secondary"
                            invert
                            onClick={(e) => handleClick(e, 'export')}
                            sx={{
                                '& svg': { mr: 1 },
                            }}
                        >
                            <FontAwesomeIcon icon={faFileExport} />
                            {kedo.t('Export')}
                        </StyledButton>
                    )}

                    {/* More actions dropdown */}
                    <StyledButton
                        variant={'outlined'}
                        color="secondary"
                        invert
                        aria-controls="more-actions-menu"
                        aria-haspopup="true"
                        onClick={handleMenuOpen}
                        sx={{
                            '&.MuiButtonGroup-middleButton': {
                                borderRightColor: 'inherit',
                                borderTopRightRadius: 'inherit',
                                borderBottomRightRadius: 'inherit',
                            },

                            '& svg': { mr: 1 },
                        }}
                    >
                        <FontAwesomeIcon icon={faBars} />
                        {kedo.t('More actions')}
                    </StyledButton>
                    <Menu
                        id="more-actions-menu"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                    >
                        <MenuItem
                            onClick={() => handleMenuSelect('subscribe')}
                            sx={{
                                '& svg': { mr: 1 },
                            }}
                        >
                            <FontAwesomeIcon icon={faBell} />
                            {kedo.t('Subscription')}
                        </MenuItem>
                        {hasSubscriptions && (
                            <MenuItem
                                onClick={() =>
                                    handleMenuSelect('stopsubscription')
                                }
                                sx={{
                                    '& svg': { mr: 1 },
                                }}
                            >
                                <FontAwesomeIcon icon={faBellSlash} />
                                {kedo.t('Stop subscription')}
                            </MenuItem>
                        )}
                        <MenuItem
                            onClick={() => handleMenuSelect('favorite')}
                            sx={{
                                '& svg': { mr: 1 },
                            }}
                        >
                            <FontAwesomeIcon icon={faStar} />
                            {kedo.t('Favorite')}
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleMenuSelect('removefavorite')}
                            sx={{
                                '& svg': { mr: 1 },
                            }}
                        >
                            <FontAwesomeIcon icon={faStar} />
                            {kedo.t('Remove favorite')}
                        </MenuItem>
                        {isAllowedDd('merge') && selected.length === 2 && (
                            <MenuItem
                                onClick={() => handleMenuSelect('merge')}
                                sx={{
                                    '& svg': { mr: 1 },
                                }}
                            >
                                <FontAwesomeIcon icon={faObjectSubtract} />
                                {kedo.t('Merge')}
                            </MenuItem>
                        )}
                        {isAllowedDd('massupdate') && (
                            <MenuItem
                                disabled
                                sx={{
                                    '& svg': { mr: 1 },
                                }}
                            >
                                <FontAwesomeIcon icon={faPenToSquare} />
                                {kedo.t('Mass update')}
                            </MenuItem>
                        )}
                        {dearchivedDossiers && isAllowedDd('archive') && (
                            <MenuItem
                                onClick={() => handleMenuSelect('archive')}
                                sx={{
                                    '& svg': { mr: 1 },
                                }}
                            >
                                <FontAwesomeIcon icon={faFolder} />
                                {kedo.t('Archive')}
                            </MenuItem>
                        )}
                        {archivedDossiers && isAllowedDd('archive') && (
                            <MenuItem
                                onClick={() => handleMenuSelect('dearchive')}
                                sx={{
                                    '& svg': { mr: 1 },
                                }}
                            >
                                <FontAwesomeIcon icon={faFolder} />
                                {kedo.t('Dearchive')}
                            </MenuItem>
                        )}
                    </Menu>
                </ButtonGroup>
            )}
            <Stack direction="row" spacing={0.5}>
                {props.multiAdd ? <>{props.multiAdd}</> : null}
                {(mode === 'embeddedList' || mode === 'block') &&
                props.addDossier ? (
                    <>{props.addDossier}</>
                ) : null}
                {mode === 'embeddedList' && props.filterDossier ? (
                    <>{props.filterDossier}</>
                ) : null}
                {isAllowedDd('export') &&
                dossiers?.length > 0 &&
                selected?.length <= 0 ? (
                    <StyledButton
                        onClick={(event) => {
                            event.preventDefault()
                            setShowActionModal('all-export')
                        }}
                        title={kedo.t('Export')}
                        variant={'outlined'}
                        color="secondary"
                        invert
                    >
                        <FontAwesomeIcon icon={faFileExport} />
                    </StyledButton>
                ) : null}
                {dossiers?.length > 0 && selected?.length <= 0 ? (
                    <>
                        <ShowEntries
                            pager={pager}
                            value={limit}
                            onChange={handleEntriesChange}
                        />
                    </>
                ) : null}
                {showActionModal === 'delete' ? (
                    <MultiContentDossierDelete
                        kedo={kedo}
                        showModal={true}
                        size={'sm'}
                        onCancel={() => {
                            setShowActionModal('')
                        }}
                        onClose={() => {
                            setShowActionModal('')
                            onDeleteSuccess()
                        }}
                        items={selected}
                        itemName={deleteItemName}
                        mode={mode}
                    />
                ) : null}
                {showActionModal === 'all-export' ? (
                    <ContentDossierExport
                        showModal={showActionModal === 'all-export'}
                        defDossierId={defDossierId}
                        disablePDF={true}
                        disableWord={true}
                        filter={getSearchFilter}
                        size={'sm'}
                        kedo={kedo}
                        onCancel={() => {
                            setShowActionModal('')
                        }}
                        onClose={() => {
                            setShowActionModal('')
                            fetchDossierResults()
                        }}
                        selected={[]}
                        mode={
                            mode === 'embeddedList' || mode === 'block'
                                ? 'all'
                                : null
                        }
                    />
                ) : null}
                {showActionModal === 'export' ? (
                    <ContentDossierExport
                        showModal={showActionModal === 'export'}
                        defDossierId={defDossierId}
                        filter={getSearchFilter}
                        size={'sm'}
                        kedo={kedo}
                        onCancel={() => {
                            setShowActionModal('')
                        }}
                        onClose={() => {
                            setShowActionModal('')
                            fetchDossierResults()
                        }}
                        selected={selected}
                        mode={
                            mode === 'embeddedList' || mode === 'block'
                                ? 'all'
                                : null
                        }
                    />
                ) : null}
                {showActionModal === 'archive' ||
                showActionModal === 'dearchive' ? (
                    <MultiContentDossierArchive
                        kedo={kedo}
                        showModal={true}
                        onClose={() => {
                            setShowActionModal('')
                            fetchDossierResults()
                        }}
                        archived={showActionModal === 'archive'}
                        checked={selected}
                        mode={mode}
                    />
                ) : null}
                {showActionModal === 'merge' ? (
                    <MultiContentDossierMerge
                        props={props}
                        kedo={kedo}
                        defDossierId={defDossierId}
                        closeModal={() => {
                            setShowActionModal('')
                            fetchDossierResults()
                        }}
                        idDossiers={selectedDossiers.map((item) => item.id)}
                    />
                ) : null}
                {showActionModal === 'subscribe' ||
                showActionModal === 'stopsubscription' ? (
                    <MultiContentDossierSubscribe
                        kedo={kedo}
                        showModal={true}
                        onClose={() => {
                            setShowActionModal('')
                            fetchDossierResults()
                        }}
                        subscriptions={subscriptions}
                        items={selected}
                        showActionModal={showActionModal}
                        mode={mode}
                    />
                ) : null}
                {showActionModal === 'favorite' ||
                    (showActionModal === 'removefavorite' && (
                        <MultiContentDossierFavorite
                            kedo={kedo}
                            showModal={true}
                            showActionModal={showActionModal}
                            onClose={() => {
                                setShowActionModal('')
                                fetchDossierResults()
                            }}
                            favorites={favorites}
                            items={selected}
                        />
                    ))}
                {showActionModal === 'massupdate' && (
                    <MultiContentDossierMassUpdate
                        kedo={kedo}
                        showModal={true}
                        onClose={() => {
                            setShowActionModal('')
                            fetchDossierResults()
                        }}
                        mode={mode}
                        selectedDossierIds={
                            selectedDossiers?.length > 0
                                ? selectedDossiers?.map((ids) => ids.id)
                                : dossiers?.map((ids) => ids.id)
                        }
                        contents={contents}
                    />
                )}
            </Stack>
        </>
    )
}

export default ActionButtons
